import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { getScamBySlug } from '@/hooks/useScam';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import ScamSidebar from '@/components/ScamSidebar';
import Breadcrumbs from '@/components/Breadcrumbs';
import { displayCategoryName } from '@/lib/scamCategories';
import { LifeBuoy, Printer } from 'lucide-react';

export const Route = createFileRoute('/_auth/scam-library/$slug')({
  component: RouteComponent,
});

const CHANNEL_LABELS: Record<string, string> = {
  EMAIL: 'Email',
  PHONE: 'Phone call',
  SMS: 'Text message',
  SOCIAL_MEDIA: 'Social media',
  IN_PERSON: 'In person',
  ONLINE: 'Online',
  POST: 'Post',
};

function cleanQuillHtml(html: string) {
  html = html.replace(/<span class="ql-ui">.*?<\/span>/g, '');
  html = html.replace(/<p>(\s|&nbsp;)*<\/p>/g, '');
  return html;
}

function DetailSkeleton() {
  return (
    <div className="rounded-card border border-slate-200/80 bg-white p-6 sm:p-10 shadow-card animate-pulse">
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-2xl bg-slate-100" />
        <div className="h-8 w-2/3 rounded bg-slate-100" />
      </div>
      <div className="mt-8 space-y-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className={`h-4 rounded bg-slate-100/70 ${i % 3 === 2 ? 'w-3/4' : 'w-full'}`} />
        ))}
      </div>
    </div>
  );
}

function RouteComponent() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const { data: scam, isLoading, isError, error, refetch } = getScamBySlug(slug);

  if (isError) {
    const status = (error as any)?.response?.status;
    if (status === 403) {
      navigate({ to: '/', replace: true, search: { invitationcode: undefined, __clerk_ticket: undefined } });
      return null;
    }
  }

  const categoryName = (scam as any)?.scamCategory?.name as string | undefined;
  const channelLabels: string[] = ((scam as any)?.channels ?? [])
    .map((c: string) => CHANNEL_LABELS[c])
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Scam Library', href: '/scam-library' },
          ]}
        />

        {isLoading ? (
          <div className="mt-4">
            <DetailSkeleton />
          </div>
        ) : isError || !scam ? (
          <div className="mt-4 rounded-card border border-slate-200/80 bg-white px-6 py-14 text-center shadow-card">
            <p className="text-[17px] text-slate-700">
              {isError ? "We couldn't load this scam just now." : "We couldn't find that scam."}
            </p>
            <div className="mt-5 flex items-center justify-center gap-4">
              {isError && (
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="inline-flex min-h-11 cursor-pointer items-center rounded-full bg-violet-600 px-6 py-2.5 text-base font-medium text-white transition-colors hover:bg-violet-700"
                >
                  Try again
                </button>
              )}
              <Link
                to="/scam-library"
                search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                className="py-2 text-base font-medium text-violet-700 underline underline-offset-4 hover:text-violet-800"
              >
                Back to the library
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:gap-0">
            {/* Article */}
            <article className="min-w-0 flex-1 lg:pr-12">
              <div className="rounded-card border border-slate-200/80 bg-white p-5 sm:p-10 shadow-card">
                {/* Header */}
                <header>
                  <div className="flex items-start gap-4 sm:gap-5">
                    {scam.image && (
                      <img
                        src={scam.image}
                        alt=""
                        loading="lazy"
                        className="h-16 w-16 shrink-0 rounded-2xl bg-slate-50 object-cover md:h-20 md:w-20"
                      />
                    )}
                    <div className="min-w-0">
                      <h1 className="text-3xl font-semibold leading-[1.15] tracking-tight text-slate-900 sm:text-4xl">
                        {scam.title}
                      </h1>
                      {(categoryName || channelLabels.length > 0) && (
                        <div className="mt-3 flex flex-wrap items-center gap-1.5">
                          {categoryName && (
                            <span className="inline-block rounded-full bg-violet-50 px-2.5 py-0.5 text-[13px] font-medium text-violet-700">
                              {displayCategoryName(categoryName)}
                            </span>
                          )}
                          {channelLabels.map((label) => (
                            <span
                              key={label}
                              className="inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-[13px] font-medium text-slate-600"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {scam.description && (
                    <p className="mt-6 border-l-2 border-violet-200 pl-4 text-lg leading-relaxed text-slate-600">
                      {scam.description}
                    </p>
                  )}
                </header>

                {/* Body */}
                <div className="prose prose-slate quill-content mt-8 max-w-none text-lg leading-relaxed text-slate-800">
                  {parse(DOMPurify.sanitize(cleanQuillHtml(scam.content)))}
                </div>
              </div>

              {/* On phones the desktop sidebar is hidden, so the response
                  actions must live here: this audience is mobile-heavy and
                  "what do I do right now?" can't be desktop-only. */}
              <div className="mt-6 rounded-card bg-violet-50 px-5 py-5 sm:px-6 lg:hidden">
                <p className="text-[17px] font-semibold text-violet-900">
                  Facing this scam right now?
                </p>
                <p className="mt-1 text-base leading-relaxed text-violet-800">
                  Stop all contact and don't send money or share details.
                </p>
                <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
                  <Link
                    to="/toolkit"
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-violet-600 px-5 py-2.5 text-base font-medium text-white transition-colors hover:bg-violet-700"
                  >
                    <Printer className="h-4 w-4" />
                    Open the Emergency Toolkit
                  </Link>
                  <Link
                    to="/contact-support"
                    search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-violet-200 bg-white px-5 py-2.5 text-base font-medium text-violet-700 transition-colors hover:bg-violet-100"
                  >
                    <LifeBuoy className="h-4 w-4" />
                    Talk to a real person
                  </Link>
                </div>
              </div>
            </article>

            {/* Sidebar (desktop) */}
            <ScamSidebar currentScamId={scam.id} scamTitle={scam.title} />
          </div>
        )}
      </div>
    </div>
  );
}
